import { useEffect, useMemo, useState } from "react";
import { useMediaQuery, Dialog, DialogContent, DialogTitle, DialogActions, Divider } from "@mui/material";

import { Box, Grid, IconButton, InputAdornment, TextField, Autocomplete, Button } from "@mui/material";
import { DatePicker, LocalizationProvider, } from '@mui/x-date-pickers';
import { Clear, Close, FilterList, Search } from "@mui/icons-material";

import moment from 'moment';
import { useTranslation } from "react-i18next";
import MomentUtils from "@date-io/moment";
import { MerchantAutocomplete } from "src/_modules/common/ServerAutocomplete";
import { ROLES } from "src/constants";
import { useSelector } from "react-redux";
import { useLazyGetCodTransactionsXlsByAdminQuery, useLazyGetCodTransactionsXlsQuery } from "src/_api/mapitShipInvoice";
import { isEmpty, omit } from "lodash";
import { DownloadExcelDialog } from "src/_modules/common";
import { LoadingButton } from "@mui/lab";

export default function CodTransactionsFilter({
    params,
    setParams,
    handleChange,
    disableExcelSend = false,
    onSubmit,
    isLoading,
    ...props
}) {

    const { t } = useTranslation();
    const { profile } = useSelector(state => state.entities.profile)
    const isDownMd = useMediaQuery(theme => theme.breakpoints.down('md'));
    const [open, setOpen] = useState(false);

    //======= Excel Download Handlers =======

    const [openExcelDialog, setOpenExcelDialog] = useState(false)
    const [isXlsLoading, setIsXlsLoading] = useState(false)
    const [isXlsSuccess, setIsXlsSuccess] = useState(false)
    const [xlsError, setXlsError] = useState(null)

    const [getCodTransactionsXls] = useLazyGetCodTransactionsXlsQuery()

    const [getCodTransactionsXlsByAdmin] = useLazyGetCodTransactionsXlsByAdminQuery()

    const closeDownloadExcelDialogHandler = () => {
        setOpenExcelDialog(false)
        setIsXlsLoading(false)
        setIsXlsSuccess(false)
        setXlsError(null)
    }

    const sendExcelFileToEmailHandler = async ({ email }) => {
        try {
            if (profile?.role === ROLES.admin) {
                console.log("Sending Excel file for admin transactions", { ...omit(params, ["page", "limit"]), email });
                await getCodTransactionsXlsByAdmin({ ...omit(params, ["page", "limit"]), email }).unwrap();
            } else {
                await getCodTransactionsXls({ ...omit(params, ["page", "limit"]), email }).unwrap();
            }

            setIsXlsLoading(true)

            setTimeout(() => {
                closeDownloadExcelDialogHandler()
            }, 2500);

        } catch (error) {
            console.error("Error sending Excel file:", error)
            setXlsError(error)
        }
    }

    const [snapshot, setSnapshot] = useState({})

    useEffect(() => {
        if (open) {
            setSnapshot(params)
        } else {
            setSnapshot({})
        }
    }, [open])

    const onCLoseHandler = () => {
        setParams(snapshot)
        setOpen(false)
    }

    const hasActiveFilters = useMemo(() => {
        return Object.values(params).some(
            value => !isEmpty(value)
        )
    }, [params])

    const ExcelButtonDialog = (
        <>
            <DownloadExcelDialog
                open={openExcelDialog}
                onSend={sendExcelFileToEmailHandler}
                onClose={closeDownloadExcelDialogHandler}
                loading={isXlsLoading}
                isSuccess={isXlsSuccess}
                error={xlsError}
                disabledSend={isXlsSuccess}
                options={["EMAIL"]}
            />
            <Button
                variant="contained"
                color="success"
                onClick={() => {
                    setOpenExcelDialog(true)
                }}
            >
                {t("Excel")}
            </Button>
        </>
    )

    const filterContent = (
        <Grid container spacing={2} alignItems="center">
            <LocalizationProvider dateAdapter={MomentUtils}>
                <Grid item md={1.8} sm={6} xs={12}>
                    <DatePicker
                        inputFormat="DD/MM/YYYY"
                        error={true}
                        maxDate={params?.fromDate ? params?.fromDate : moment()}
                        label={t("From")}
                        value={params?.fromDate ?? null}
                        onChange={(newValue) => {
                            handleChange({ target: { name: "fromDate", value: newValue } })
                        }}
                        renderInput={(InputParams) => {
                            InputParams.InputProps = {
                                ...InputParams.InputProps,
                                startAdornment: !!params?.fromDate ?
                                    <InputAdornment position="start">
                                        <IconButton onClick={(event) => {
                                            event.stopPropagation()
                                            handleChange({ target: { name: "fromDate", value: null } })
                                        }} size='small' color='primary'>
                                            <Clear sx={{ fontSize: "inherit" }} />
                                        </IconButton>
                                    </InputAdornment> : '',
                            }
                            return <TextField fullWidth size="small" {...InputParams} />
                        }}
                    />
                </Grid>
                <Grid item md={1.8} sm={6} xs={12}>
                    <DatePicker
                        inputFormat="DD/MM/YYYY"
                        maxDate={moment()}
                        minDate={params?.toDate ? params?.toDate : moment().subtract(1, 'y')}
                        label={t("To")}
                        value={params?.toDate ?? null}
                        onChange={(newValue) => {
                            handleChange({ target: { name: "toDate", value: newValue } })
                        }}
                        renderInput={(InputParams) => {
                            InputParams.InputProps = {
                                ...InputParams.InputProps,
                                startAdornment: !!params?.toDate ?
                                    <InputAdornment position="start">
                                        <IconButton onClick={(event) => {
                                            event.stopPropagation()
                                            handleChange({ target: { name: "toDate", value: null } })
                                        }} size='small' color='primary'>
                                            <Clear sx={{ fontSize: "inherit" }} />
                                        </IconButton>
                                    </InputAdornment> : '',
                            }
                            return <TextField fullWidth size="small" {...InputParams} />
                        }}
                    />
                </Grid>
            </LocalizationProvider>
            {
                profile?.role === ROLES.admin &&
                <Grid item md={2.2} sm={4} xs={12}>
                    <MerchantAutocomplete
                        deliveryMethod="MAPIT_SHIP"
                        name="merchant"
                        value={params?.merchant}
                        onChange={handleChange}
                    />
                </Grid>
            }
            <Grid item md={2.2} sm={4} xs={12}>
                <Autocomplete
                    multiple
                    options={["PENDING", "COMPLETED", "CANCELED"]}
                    value={params?.currentStatus ?? []}
                    getOptionLabel={(option) => t(`codTransactionStatus.${option}`)}
                    onChange={(event, newValue) => {
                        handleChange({ target: { name: "currentStatus", value: newValue } });
                    }}
                    ChipProps={{
                        variant: 'outlined',
                        color: 'primary',
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label={t("Status")}
                            placeholder={t("Status")}
                            size="small"
                        />
                    )}
                />
            </Grid>
            <Grid item md={2.2} sm={4} xs={12}>
                <Autocomplete
                    multiple
                    options={["WITHDRAWAL", "WALLET_TRANSFER", "DEPOSIT", "WITHDRAWAL_CANCELED"]}
                    value={params?.category ?? []}
                    getOptionLabel={(option) => t(`codTransactionCategory.${option}`)}
                    onChange={(event, newValue) => {
                        handleChange({ target: { name: "category", value: newValue } });
                    }}
                    ChipProps={{
                        variant: 'outlined',
                        color: 'primary',
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label={t("Category")}
                            placeholder={t("Category")}
                            size="small"
                        />
                    )}
                />
            </Grid>
            {
                !isDownMd &&
                <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <LoadingButton loading={isLoading} variant="contained" color="primary" onClick={onSubmit} startIcon={<Search />} sx={{ mr: 1 }}>
                        {t("Search")}
                    </LoadingButton>
                    {ExcelButtonDialog}
                </Grid>
            }
        </Grid>
    );

    if (isDownMd) {
        return (
            <>
                <Box display={{ xs: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box position="relative">
                        <IconButton variant="outlined" color="primary" onClick={() => setOpen(true)}>
                            <FilterList />
                        </IconButton>
                        {hasActiveFilters && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    width: 8,
                                    height: 8,
                                    bgcolor: 'error.main',
                                    borderRadius: '50%',
                                    zIndex: 2,
                                }}
                            />
                        )}
                    </Box>
                    {ExcelButtonDialog}
                </Box>
                <Dialog open={open} onClose={e => e.preventDefault()} fullWidth maxWidth="md" fullScreen={isDownMd}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {t("Filter")}
                        <IconButton size='small' onClick={onCLoseHandler}>
                            <Close fontSize='inhert' />
                        </IconButton>
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        {filterContent}
                    </DialogContent>
                    <Divider />
                    <DialogActions>
                        <Button variant="contained" onClick={onCLoseHandler}>{t("Close")}</Button>
                        <Box sx={{ flexGrow: 1 }} />
                        <LoadingButton
                            loading={isLoading}
                            sx={{ textTransform: "none" }}
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                onSubmit()
                                setOpen(false)
                            }}
                            startIcon={<Search />}
                        >
                            {t("Search")}
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
    return <Box {...props}>{filterContent}</Box>;
}